// Theme cache management utilities
import { useEffect, useRef } from 'react';
import { refreshThemeCache, getCacheStats, getCachedThemeNames } from './theme-cache';
import { clearThemeCacheCookie, clearThemePreferenceCookie } from './theme-cookies';

/**
 * Hook to manage theme cache lifecycle
 */
export function useThemeCacheManager(currentThemeName: string | null) {
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const cleanupIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Set up periodic cache refresh (every 30 minutes)
    if (currentThemeName) {
      refreshIntervalRef.current = setInterval(() => {
        refreshThemeCache(currentThemeName);
      }, 30 * 60 * 1000); // 30 minutes
    }

    // Set up periodic cache cleanup (every 2 hours)
    cleanupIntervalRef.current = setInterval(() => {
      performCacheCleanup();
    }, 2 * 60 * 60 * 1000); // 2 hours

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      if (cleanupIntervalRef.current) {
        clearInterval(cleanupIntervalRef.current);
      }
    };
  }, [currentThemeName]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      if (cleanupIntervalRef.current) {
        clearInterval(cleanupIntervalRef.current);
      }
    };
  }, []);
}

/**
 * Perform cache cleanup - remove expired entries and manage size
 */
export function performCacheCleanup(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const stats = getCacheStats();
    
    // Log cache statistics for debugging
    if (process.env.NODE_ENV === 'development') {
      console.log('Theme cache stats:', stats);
    }

    // If cache is getting too large, trigger cleanup
    if (stats.cacheSize > 100000) { // 100KB threshold
      console.warn('Theme cache is getting large, consider clearing expired entries');
    }

    // Check for corrupted cache entries
    const cachedThemes = getCachedThemeNames();
    if (cachedThemes.length === 0 && stats.totalEntries > 0) {
      console.warn('Detected corrupted theme cache, clearing...');
      localStorage.removeItem('theme-cache');
    }
  } catch (error) {
    console.warn('Error during cache cleanup:', error);
  }
}

/**
 * Clear all theme caches (emergency cleanup)
 */
export function clearAllThemeCaches(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    // Clear localStorage cache
    localStorage.removeItem('theme-cache');
    
    // Clear cookies
    clearThemeCacheCookie();
    clearThemePreferenceCookie();
    
    console.log('All theme caches cleared');
  } catch (error) {
    console.error('Error clearing theme caches:', error);
  }
}

/**
 * Preload themes for better performance
 */
export async function preloadThemes(themeNames: string[]): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }

  const preloadPromises = themeNames.map(async (themeName) => {
    try {
      await refreshThemeCache(themeName);
    } catch (error) {
      console.warn(`Failed to preload theme ${themeName}:`, error);
    }
  });

  await Promise.all(preloadPromises);
}

/**
 * Get cache health information
 */
export function getCacheHealth(): {
  isHealthy: boolean;
  issues: string[];
  recommendations: string[];
} {
  const issues: string[] = [];
  const recommendations: string[] = [];

  try {
    const stats = getCacheStats();
    
    // Check cache size
    if (stats.cacheSize > 200000) { // 200KB
      issues.push('Cache size is very large');
      recommendations.push('Consider clearing old cache entries');
    }

    // Check for expired entries
    if (stats.expiredEntries > 0) {
      issues.push(`${stats.expiredEntries} expired cache entries found`);
      recommendations.push('Run cache cleanup to remove expired entries');
    }

    // Check cache hit ratio (if we have tracking)
    if (stats.validEntries === 0 && stats.totalEntries > 0) {
      issues.push('No valid cache entries found');
      recommendations.push('Clear corrupted cache and refresh');
    }

    return {
      isHealthy: issues.length === 0,
      issues,
      recommendations
    };
  } catch (error) {
    return {
      isHealthy: false,
      issues: ['Error reading cache health'],
      recommendations: ['Clear all caches and restart']
    };
  }
}

/**
 * Initialize cache warming on app start
 */
export function warmThemeCache(availableThemes: string[]): void {
  if (typeof window === 'undefined') {
    return;
  }

  // Warm cache in the background after a short delay
  setTimeout(() => {
    preloadThemes(availableThemes.slice(0, 3)); // Preload first 3 themes
  }, 1000);
}